<template>
  <div id="app" data-v-app="">
    <div class="row-wrapper">
      <div class="col-8">
        <div class="image"></div>
        <div class="row-wrapper" id="picture" :style="backgroundStyle"></div>
      </div>
      <div class="col-4">
        <div id="login-card" class="card-body">
          <div class="logo-brand">
            <img id="logo-brand-omnih" :src="corporateLogo" alt="logo">
            <hr class="separator">
          </div>

          <!-- Standard Login Form -->
          <o-form id="form-login" class="form-login" v-if="!isOtpMode">
            <o-form-group title="Email" name="email">
              <o-input-text 
                name="email" 
                v-model="email" 
                type="email" 
                :required="true" 
                placeholder="email"
                :disabled="isLoading"
              />
            </o-form-group>

            <o-form-group title="Password" name="password" @keyup.enter="submitLogin()">
              <o-input-text 
                name="password" 
                v-model="password" 
                :type="showPassword ? 'text' : 'password'" 
                :required="true"
                placeholder="password"
                :disabled="isLoading"
              />
            </o-form-group>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="showPasswordCheckbox" v-model="showPassword">
              <label class="form-check-label" for="showPasswordCheckbox" style="color: #4a5568">
                Show Password
              </label>
            </div>

            <div class="d-flex flex-column align-items-end gap-2 mb-2">
              <router-link :to="{ name: 'ExtranetForgotPassword' }">
                <small>Forgot password?</small>
              </router-link>
              <a href="javascript:void(0);" class="otp-link" @click="switchToOtpMode">
                Login with OTP
              </a>
            </div>

            <!-- Error Message -->
            <div v-if="errorMessage" class="custom-alert">
              <div class="alert-content">
                <span class="alert-icon">&#9888;</span>
                <p>{{ errorMessage }}</p>
              </div>
            </div>

            <div class="d-flex justify-content-center" id="login-container">
              <o-button 
                class="login-button" 
                v-if="!isLoading" 
                :size="'block'" 
                @click="submitLogin"
              >
                Login
              </o-button>
              <o-button v-else :disabled="true" :size="'block'">
                Loading...
              </o-button>
            </div>
          </o-form>

          <!-- OTP Login Form -->
          <o-form id="form-otp" v-if="isOtpMode">
            <o-form-group title="Email" name="otp-email" v-if="!otpRequested">
              <o-input-text v-model="otpEmail" type="email" :required="true" placeholder="Enter your email"/>
            </o-form-group>

            <div v-if="!otpRequested" class="d-flex justify-content-center mt-3">
              <o-button v-if="!isLoading" class="otp-button" @click="requestOtp">Send OTP</o-button>
              <o-button v-else :disabled="true" class="otp-button">
                Loading...
              </o-button>
            </div>

            <div v-if="otpRequested" class="d-flex flex-column gap-3 mt-3">
              <o-form-group title="Enter OTP" name="otp">
                <o-input-digit v-model="otp" :required="true" placeholder="Enter OTP"/>
              </o-form-group>

              <div class="d-flex flex-column align-items-center gap-2">
                <div v-if="showCountdown">
                  <span class="countdown-text">{{ formattedTime }}</span>
                </div>
                <o-button class="resend-button" v-if="!showCountdown" @click="resendOTP">Resend OTP</o-button>
              </div>

              <div class="d-flex justify-content-center">
                <o-button v-if="!isLoading" class="otp-button" @click="submitOtp">Submit OTP</o-button>
                <o-button v-else :disabled="true" class="otp-button">
                  Loading...
                </o-button>
              </div>

              <div v-if="otpRequested && otpEmail" class="text-center mt-2">
                <p class="custom-label">
                  *Please check your <strong>{{ otpEmail }}</strong> mailbox to get the OTP code.
                </p>
              </div>
            </div>

            <div v-if="otpErrorMessage" class="custom-alert text-center mt-2">
              <span class="alert-icon">&#9888;</span>
              <p>{{ otpErrorMessage }}</p>
            </div>

            <div class="d-flex justify-content-center mt-3">
              <o-button class="otp-back-button" variant="secondary" @click="switchToLoginMode">
                Back to Login
              </o-button>
            </div>
          </o-form>

          <div class="simple-footer">
            <small>
              Copyright
              <span>©</span>
              PT. Omni Hotelier Internasional
              <span>{{ currentYear }}</span>
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { OForm, OFormGroup, OInputText, OButton, OInputDigit } from '@/components/OmnihControl'

export default {
  name: 'ExtranetLogin',
  components: {
    OForm,
    OFormGroup,
    OInputText,
    OButton,
    OInputDigit,
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    // Login state
    const email = ref('')
    const password = ref('')
    const showPassword = ref(false)
    const isLoading = ref(false)
    const errorMessage = ref('')
    const currentYear = new Date().getFullYear()
    const corporateLogo = ref('/img/logos/extranet-logo.avif')
    const loginBackground = ref('/img/temporary-login.png')

    // OTP state
    const isOtpMode = ref(false)
    const otpEmail = ref('')
    const otp = ref('')
    const otpRequested = ref(false)
    const otpErrorMessage = ref('')
    const showCountdown = ref(false)
    const countdownMinutes = ref(2)
    const countdownSeconds = ref(0)
    const countdownInterval = ref(null)

    const backgroundStyle = computed(() => ({
      backgroundImage: `url(${loginBackground.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }))

    const formattedTime = computed(() => {
      const minutes = countdownMinutes.value
      const seconds = countdownSeconds.value < 10 ? `0${countdownSeconds.value}` : countdownSeconds.value
      return `${minutes}:${seconds}`
    })

    // OTP Mode Switching
    const switchToOtpMode = () => {
      isOtpMode.value = true
      otpRequested.value = false
      otpEmail.value = ''
      otp.value = ''
      otpErrorMessage.value = ''
      errorMessage.value = ''
    }

    const switchToLoginMode = () => {
      isOtpMode.value = false
      email.value = ''
      password.value = ''
      otpRequested.value = false
      otpEmail.value = ''
      otp.value = ''
      otpErrorMessage.value = ''
      errorMessage.value = ''
      showCountdown.value = false
      clearInterval(countdownInterval.value)
    }

    // Countdown Timer
    const startCountdown = () => {
      showCountdown.value = true
      countdownMinutes.value = 2
      countdownSeconds.value = 0
      clearInterval(countdownInterval.value)
      countdownInterval.value = setInterval(() => {
        if (countdownSeconds.value > 0) {
          countdownSeconds.value--
          return
        }
        if (countdownMinutes.value > 0) {
          countdownMinutes.value--
          countdownSeconds.value = 59
          return
        }
        clearInterval(countdownInterval.value)
        showCountdown.value = false
      }, 1000)
    }

    // Request OTP - uses extranet endpoint
    const requestOtp = async () => {
      if (!otpEmail.value) {
        otpErrorMessage.value = 'Please enter a valid email to receive OTP.'
        return
      }

      isLoading.value = true
      otpErrorMessage.value = ''

      try {
        const data = {
          email: otpEmail.value,
          extranet: true,
        }
        await store.dispatch('Member/extranet_request_otp', data)
        otpRequested.value = true
        otpErrorMessage.value = ''
        startCountdown()
      } catch (error) {
        if (error.response && error.response.data) {
          const serverResponse = error.response.data
          otpErrorMessage.value = serverResponse.error || serverResponse.message || 'Failed to send OTP. Please try again.'
        } else if (error.message) {
          otpErrorMessage.value = error.message
        } else {
          otpErrorMessage.value = 'Failed to send OTP. Please try again.'
        }
      } finally {
        isLoading.value = false
      }
    }

    // Resend OTP
    const resendOTP = async () => {
      otpErrorMessage.value = ''
      await requestOtp()
      startCountdown()
    }

    // Submit OTP - uses extranet endpoints
    const otpErrorMap = {
      'AUTH.LOGIN.INVALID.AUTH.FAIL.LEVEL': 'Invalid level. Please log in with a valid account.',
      'EXTRANET.AUTH.LOGIN.ACCESS_DENIED': 'Access denied. Only root administrators can access Extranet.',
      'EXTRANET.AUTH.LOGIN.ACCOUNT_NOT_FOUND': 'Account not found.',
    }

    const submitOtp = async () => {
      if (!otp.value) {
        otpErrorMessage.value = 'Please enter the OTP code.'
        return
      }

      isLoading.value = true
      otpErrorMessage.value = ''

      try {
        // Step 1: Submit OTP to verify
        await store.dispatch('Member/extranet_submit_otp', {
          email: otpEmail.value,
          otp: otp.value,
          otp_login: 'login',
        })

        // Step 2: Check login via extranet context
        const data = {
          email: otpEmail.value,
          otp_login: 'login',
          extranet: true,
        }
        await store.dispatch('Auth/extranetCheckLogin', data)

        // Step 3: Redirect to extranet dashboard
        router.push({ name: 'ExtranetDashboard' })
      } catch (err) {
        const msg = err.response?.data?.message
        otpErrorMessage.value = otpErrorMap[msg] || err.response?.data?.error || 'Failed to verify OTP.'
      } finally {
        isLoading.value = false
      }
    }

    // Standard Login
    const submitLogin = async () => {
      if (isLoading.value) return

      if (!email.value || !password.value) {
        errorMessage.value = 'Please enter email and password'
        return
      }

      isLoading.value = true
      errorMessage.value = ''

      try {
        const loginData = {
          email: email.value,
          password: password.value,
          merchant_id: '',
          corporate_id: '',
          dashboard: false,
          extranet: true,
        }

        const res = await store.dispatch('Auth/login', loginData)

        if (res?.requires_2fa) {
          isLoading.value = false
          return
        }

        router.push({ name: 'ExtranetDashboard' })
      } catch (err) {
        if (err.response?.data?.message) {
          const msg = err.response.data.message
          const errorMessages = {
            'EXTRANET.AUTH.LOGIN.ACCOUNT_NOT_FOUND': 'Account not found',
            'EXTRANET.AUTH.LOGIN.INVALID_CREDENTIALS': 'Invalid email or password',
            'EXTRANET.AUTH.LOGIN.ACCESS_DENIED': 'Access denied. Only root administrators can access Extranet.',
            'EXTRANET.AUTH.LOGIN.ACCOUNT_INACTIVE': 'Your account is inactive',
            'EXTRANET.AUTH.LOGIN.ROOT_NOT_FOUND': 'Root user not found',
            'EXTRANET.AUTH.LOGIN.VALIDATION_ERROR': err.response.data.error || 'Validation error',
          }
          errorMessage.value = errorMessages[msg] || err.response.data.error || msg
        } else {
          errorMessage.value = 'Network error. Please try again later.'
        }
      } finally {
        isLoading.value = false
      }
    }

    // Cleanup on unmount
    onBeforeUnmount(() => {
      clearInterval(countdownInterval.value)
    })

    return {
      email,
      password,
      showPassword,
      isLoading,
      errorMessage,
      currentYear,
      corporateLogo,
      loginBackground,
      backgroundStyle,
      submitLogin,
      // OTP
      isOtpMode,
      otpEmail,
      otp,
      otpRequested,
      otpErrorMessage,
      showCountdown,
      formattedTime,
      switchToOtpMode,
      switchToLoginMode,
      requestOtp,
      resendOTP,
      submitOtp,
    }
  }
}
</script>


<style scoped>
.form-login {
  margin-top: 1%;
}
.row-wrapper {
  display: flex;
  width: 100%;
  height: 100vh;
}
#picture {
  align-items: center;
}
.col-8 {
  width: 66.66%;
  display: grid;
  align-items: center;
  position: relative;
  overflow: hidden;
}
.image {
  height: 50%;
  object-fit: contain;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  top: 25%;
  left: 0;
  right: 0;
  bottom: 0;
}
.col-4 {
  width: 33.33%;
  display: flex;
  justify-content: center;
  align-items: start;
  position: relative;
  background-color: white;
  height: 100vh;
  box-shadow: -10px 0 20px rgba(0, 0, 0, 0.1);
}
.card-body#login-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: small;
  width: 100%;
  max-width: 400px;
  padding: 2rem;
  overflow: auto;
}
.logo-brand {
  justify-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
#logo-brand-omnih {
  display: block;
  width: 85%;
  margin-bottom: 1rem;
}
.separator {
  border: none;
  height: 1px;
  background-color: #e2e8f0;
  width: 100%;
  margin: 1.5rem 0;
}
#login-container {
  display: flex;
  margin-top: 3rem;
}
.simple-footer {
  margin-top: 20dvh;
  text-align: center;
  color: #98a6ad;
  display: flex;
  justify-content: center;
}
small {
  display: inline-block;
  width: 100%;
  text-align: center;
}
.login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  width: 50%;
  font-size: 16px;
  color: white;
  background: linear-gradient(135deg, #ff416c, #ff4b2b);
  border-radius: 8px;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(255, 65, 108, 0.3);
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}
.login-button:hover {
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  transform: scale(1.02);
  color: white;
}
.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}
.custom-alert {
  position: relative;
  background: linear-gradient(135deg, #ff4b2b, #ff416c);
  color: white;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(255, 65, 108, 0.3);
  animation: fadeInDown 0.6s ease-out;
  display: flex;
  align-items: center;
  margin-top: 20px;
}
.alert-content {
  display: flex;
  align-items: center;
  width: 100%;
}
.alert-icon {
  font-size: 18px;
  margin-right: 12px;
}
.custom-alert p {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (max-width: 768px) {
  .col-8 {
    display: none;
  }
  .col-4 {
    width: 100%;
    max-width: 100%;
  }
  .row-wrapper {
    justify-content: center;
  }
  .card-body#login-card {
    margin: 10%;
  }
}

.form-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.form-check-input {
  cursor: pointer;
}
.form-check-label {
  font-size: 14px;
  cursor: pointer;
  user-select: none;
}

/* OTP & Forgot Password Links */
.otp-link {
  color: #ff416c;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.2s ease;
  cursor: pointer;
}
.otp-link:hover {
  color: #ee0979;
  text-decoration: underline;
}

/* OTP Button */
.otp-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  width: 100%;
  font-size: 14px;
  color: white;
  background: linear-gradient(135deg, #ff416c, #ff4b2b);
  border-radius: 8px;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(255, 65, 108, 0.3);
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}
.otp-button:hover {
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  transform: scale(1.02);
  color: white;
}

/* Resend Button */
.resend-button {
  background: transparent;
  border: 1px solid #ff416c;
  color: #ff416c;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.resend-button:hover {
  background: #ff416c;
  color: white;
}

/* OTP Back Button */
.otp-back-button {
  background: transparent;
  border: 1px solid #a0aec0;
  color: #4a5568;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: center;
}
.otp-back-button:hover {
  background: #edf2f7;
  border-color: #718096;
}

/* Countdown */
.countdown-text {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
}

/* Custom Label */
.custom-label {
  font-size: 12px;
  color: #718096;
  line-height: 1.5;
}
</style>

